/**
 * DialPad — numpad for manual outbound calls.
 *
 * Driven by PhoneProvider (dial via usePhoneActions). Used inside the docked
 * softphone (ExpandedDial view). Brand auto-detected from
 * dialPadPrefill → localStorage → CINCO fallback.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTelephonyClient } from '../../hooks/useTelephonyClient';
import { usePhoneActions } from '../../hooks/usePhoneActions';
import { usePhoneStore, getLastBrand } from '../../stores/phone-store';
import type { Brand } from '../../lib/telephony';
import { RecentNumbersDropdown } from './RecentNumbersDropdown';
import { ColleaguesDropdown } from './ColleaguesDropdown';
import { PhoneIcon } from './icons';

const DIAL_BUTTONS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['*', '0', '#'],
];

const KEY_LABELS: Record<string, string> = {
  '0': 'Nul',
  '1': 'Een',
  '2': 'Twee',
  '3': 'Drie',
  '4': 'Vier',
  '5': 'Vijf',
  '6': 'Zes',
  '7': 'Zeven',
  '8': 'Acht',
  '9': 'Negen',
  '*': 'Ster',
  '#': 'Hekje',
};

const BRAND_OPTIONS: Brand[] = [
  'CINCO',
  'KLEDA',
  'SCHOONHUISSHOP',
  'SOFASAVER',
  'VLOERKLEEDREINIGEN',
  'BANKREINIGEN',
];

export function DialPad({ onClose }: { onClose: () => void }) {
  const { activeCall } = useTelephonyClient();
  const { dial } = usePhoneActions();
  const dialPadPrefill = usePhoneStore((s) => s.dialPadPrefill);

  const initialBrand = useMemo<Brand>(() => {
    if (dialPadPrefill?.brand) return dialPadPrefill.brand as Brand;
    const saved = getLastBrand();
    if (saved && BRAND_OPTIONS.includes(saved as Brand)) return saved as Brand;
    return 'CINCO';
  }, [dialPadPrefill]);

  const [number, setNumber] = useState(dialPadPrefill?.number ?? '');
  const [brand, setBrand] = useState<Brand>(initialBrand);
  const [showRecents, setShowRecents] = useState(false);
  const [showColleagues, setShowColleagues] = useState(false);
  const [isDialing, setIsDialing] = useState(false);
  const [customerId, setCustomerId] = useState<string | undefined>(dialPadPrefill?.customerId);
  const [customerName, setCustomerName] = useState<string | undefined>(dialPadPrefill?.customerName);
  const [sourceContext, setSourceContext] = useState<string | undefined>(dialPadPrefill?.sourceContext);
  // Belcontext (terugbel-lus, 22-10): taak/order/reden uit de prefill, mee naar de POST.
  const [belcontext, setBelcontext] = useState<{ taskId?: string; ref?: string; reden?: string }>({
    taskId: dialPadPrefill?.contextTaskId, ref: dialPadPrefill?.contextRef, reden: dialPadPrefill?.contextReden,
  });

  useEffect(() => {
    if (dialPadPrefill?.number) setNumber(dialPadPrefill.number);
    if (dialPadPrefill?.brand) setBrand(dialPadPrefill.brand as Brand);
    if (dialPadPrefill?.customerId) setCustomerId(dialPadPrefill.customerId);
    if (dialPadPrefill?.customerName) setCustomerName(dialPadPrefill.customerName);
    if (dialPadPrefill?.sourceContext) setSourceContext(dialPadPrefill.sourceContext);
    setBelcontext({ taskId: dialPadPrefill?.contextTaskId, ref: dialPadPrefill?.contextRef, reden: dialPadPrefill?.contextReden });
  }, [dialPadPrefill]);

  async function handleDial() {
    const trimmed = number.trim();
    if (!trimmed || isDialing || activeCall) return;
    setIsDialing(true);
    try {
      await dial(trimmed, {
        brand,
        customerId,
        customerName,
        sourceContext: sourceContext ?? 'dialpad',
        contextTaskId: belcontext.taskId,
        contextRef: belcontext.ref,
        contextReden: belcontext.reden,
      });
      onClose();
    } catch (err) {
      console.error('[DialPad] call error:', err);
    }
    setIsDialing(false);
  }

  function append(key: string) {
    setNumber((prev) => prev + key);
  }

  function backspace() {
    setNumber((prev) => prev.slice(0, -1));
  }

  return (
    <div className="w-full bg-slate-800 rounded-xl shadow-2xl border border-slate-700 p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">Bellen</span>
          <button
            type="button"
            onClick={() => {
              setShowColleagues((v) => !v);
              setShowRecents(false);
            }}
            aria-pressed={showColleagues}
            className={`inline-flex items-center gap-1 text-[11px] rounded px-1.5 py-0.5 ${
              showColleagues
                ? 'bg-indigo-600 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            Collega's
          </button>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Sluit dialpad"
          className="text-slate-400 hover:text-white"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="relative mb-2">
        <div className="flex items-center gap-1">
          <input
            type="text"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="Nummer invoeren..."
            className="flex-1 bg-slate-700 text-white text-sm font-mono rounded px-2 py-1.5 placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
            onFocus={() => setShowRecents(true)}
            onBlur={() => setTimeout(() => setShowRecents(false), 150)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleDial();
              }
              if (e.key === 'Escape') onClose();
            }}
            autoFocus
          />
          {number && (
            <button
              type="button"
              onClick={backspace}
              aria-label="Wis laatste teken"
              className="text-slate-400 hover:text-white p-1"
            >
              ⌫
            </button>
          )}
        </div>
        {showColleagues && (
          <div className="absolute left-0 right-0 top-full mt-1 z-50">
            <ColleaguesDropdown
              onSelect={(c) => {
                setNumber(c.phone);
                setBrand('CINCO');
                setCustomerId(undefined);
                setCustomerName(c.name);
                setSourceContext('collega');
                setShowColleagues(false);
                setShowRecents(false);
              }}
            />
          </div>
        )}
        {showRecents && !showColleagues && number.length === 0 && (
          <div className="absolute left-0 right-0 top-full mt-1 z-50">
            <RecentNumbersDropdown
              onSelect={(r) => {
                setNumber(r.to_number);
                if (r.last_brand && BRAND_OPTIONS.includes(r.last_brand as Brand)) {
                  setBrand(r.last_brand as Brand);
                }
                if (r.last_customer_name) setCustomerName(r.last_customer_name);
                setShowRecents(false);
              }}
            />
          </div>
        )}
      </div>

      <div
        role="group"
        aria-label="Numeriek toetsenbord"
        className="grid grid-cols-3 gap-1 mb-2"
      >
        {DIAL_BUTTONS.flat().map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => append(key)}
            aria-label={KEY_LABELS[key] ?? key}
            className="py-2 text-white text-sm font-medium rounded hover:bg-slate-700 transition-colors"
          >
            {key}
          </button>
        ))}
      </div>

      <select
        value={brand}
        onChange={(e) => setBrand(e.target.value as Brand)}
        className="w-full bg-slate-700 text-white text-xs rounded px-2 py-1.5 mb-2 focus:outline-none focus:ring-1 focus:ring-indigo-500"
      >
        {BRAND_OPTIONS.map((b) => (
          <option key={b} value={b}>{b}</option>
        ))}
      </select>

      <button
        type="button"
        onClick={handleDial}
        disabled={!number.trim() || isDialing || !!activeCall}
        className="w-full py-2 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-600 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-1.5"
      >
        <PhoneIcon className="w-4 h-4 shrink-0" />
        {isDialing ? 'Verbinden...' : 'Bellen'}
      </button>
    </div>
  );
}
